import { Fragment, ReactNode, useMemo } from "react";

import {
  CheckRoundIcon,
  InfoIcon,
  WarnTriangleIcon,
} from "@vericus/cadmus-icons";
import { Icon, Text, vars } from "@vericus/cadmus-ui";

import clsx from "clsx";

import { useAppSelector } from "@/data/hooks";
import {
  PageInfo,
  selectAnswerSlice,
  selectIncompleteAnswerIds,
} from "@/features/answer";
import { StatusTag } from "@/features/answer/components/status-tag";
import { AnswerBlockFragment, QuestionType } from "@/generated/graphql";

import { groupAnswerBlocksByQuestion } from "../../utils/answer-blocks";
import * as styles from "./answer-sidebar.css";
import { PanelControl } from "./panel-control";
import { SidebarSection } from "./sidebar-section";
import { useSidebarOpenState } from "./use-sidebar-open-state";

interface Props {
  /** List of all answer in order */
  answers: AnswerBlockFragment[];
  /** Current answer page */
  page: PageInfo;
  /** Callback to select a new answer page */
  onSelectPage: (page: PageInfo) => void;
  /** Whether the navigation is locked */
  locked?: boolean;
  /** Whether this task has attached resources */
  hasResources?: boolean;
  /**
   * Prevent backtracking or jumping to out-of-order answers.
   * @default false
   */
  disableBacktrack?: boolean;
}

interface SectionItem {
  id: string | null;
  number: number;
  answers: AnswerBlockFragment[];
}

/**
 * Navigation sidebar with collapsible state.
 */
export function AnswerSidebar(props: Props) {
  const {
    page: currentPage,
    onSelectPage,
    locked = false,
    hasResources = false,
    disableBacktrack = false,
  } = props;

  // Sidebar open state
  const [isOpen, toggleOpen] = useSidebarOpenState();
  const styleVariant = isOpen ? "open" : "closed";

  const { hasDeviceCheck, deviceCheckCompleted } =
    useAppSelector(selectAnswerSlice);

  return (
    <Fragment>
      <div className={styles.panel[styleVariant]}>
        <div className={styles.root[styleVariant]}>
          <Text
            kind="label"
            color="lilac500"
            className={styles.title}
            as={"div"}
          >
            Assessment Outline
          </Text>

          <div className={styles.divider} />

          {hasDeviceCheck && (
            <Item
              page={{ page: "device-check" }}
              currentPage={currentPage}
              disableBacktrack={
                false
              } /* Must always allow access to device-check (until CDMS-3799, then we can disable again) */
              onSelectPage={onSelectPage}
            >
              <div className={styles.leftIconWrapper}>
                {deviceCheckCompleted ? (
                  <CheckRoundIcon
                    label="success"
                    size={18}
                    color={vars.colors.success}
                  />
                ) : (
                  <WarnTriangleIcon
                    label="warning"
                    size={18}
                    color={vars.colors.alert}
                  />
                )}
              </div>
              <Text kind="bodySm" color="navy500" as="span">
                Device check for Audio-Video
              </Text>
              {deviceCheckCompleted && (
                <div className={styles.status}>
                  <StatusTag answered />
                </div>
              )}
            </Item>
          )}

          <Item
            page={{ page: "overview" }}
            currentPage={currentPage}
            disableBacktrack={disableBacktrack}
            onSelectPage={onSelectPage}
          >
            <div className={styles.leftIconWrapper}>
              <InfoIcon label="info" size={24} color={vars.colors.navy400} />
            </div>
            <Text kind="bodySm" color="navy500" as="span">
              Assessment Overview
            </Text>
            {hasResources && (
              <Icon iconName="Attach" className={styles.resourcesIcon} />
            )}
          </Item>

          {!locked && <MainNavigation {...props} />}

          <div className={styles.track}>
            <PanelControl isOpen={isOpen} onClick={toggleOpen} />
          </div>
        </div>
      </div>
    </Fragment>
  );
}

// Main navigation with sections and answers, and AI page
const MainNavigation = (
  props: Pick<Props, "answers" | "page" | "onSelectPage" | "disableBacktrack">
) => {
  const { answers, page, disableBacktrack, onSelectPage } = props;

  const incompleteAnswerIds = useAppSelector(selectIncompleteAnswerIds);
  const { sections } = useMemo(() => makeSidebarSections(answers), [answers]);
  const currentAnswerId = page.page === "answer" ? page.answerId : undefined;

  return (
    <Fragment>
      <div className={styles.divider} />
      {sections.map(
        (section) =>
          section.answers.length > 0 && (
            <Fragment key={section.id ?? "section-global"}>
              <SidebarSection
                sectionId={section.id ?? undefined}
                sectionNumber={section.id ? `${section.number}` : undefined}
                answers={section.answers}
                onSelectAnswer={(bodyAnswerId) =>
                  props.onSelectPage({ page: "answer", answerId: bodyAnswerId })
                }
                selectedAnswerId={currentAnswerId}
                incompleteAnswerIds={incompleteAnswerIds}
                disableBacktrack={disableBacktrack}
              />
              <div className={styles.divider} />
            </Fragment>
          )
      )}
      <button
        className={clsx(
          styles.listItem,
          page.page === "agreement" && styles.selected,
          disableBacktrack && styles.noCursorPointer
        )}
        onClick={() =>
          disableBacktrack ? undefined : onSelectPage({ page: "agreement" })
        }
      >
        <Text kind="bodyLine" color="navy500" as="span">
          Submission Declaration
        </Text>
      </button>
    </Fragment>
  );
};

const Item = ({
  page,
  currentPage,
  disableBacktrack,
  onSelectPage,
  children,
}: {
  page: PageInfo;
  currentPage: PageInfo;
  disableBacktrack: boolean;
  onSelectPage: (page: PageInfo) => void;
  children?: ReactNode;
}) => {
  return (
    <button
      className={clsx({
        [styles.listItem]: true,
        [styles.selected]: currentPage.page === page.page,
        [styles.noCursorPointer]: disableBacktrack,
      })}
      onClick={disableBacktrack ? undefined : () => onSelectPage(page)}
    >
      {children}
    </button>
  );
};

// Section answers into sections based on section question's present in the
// ordered answer blocks.
function makeSidebarSections(answers: AnswerBlockFragment[]): {
  overview?: AnswerBlockFragment;
  sections: SectionItem[];
} {
  const groupedByQuestion = groupAnswerBlocksByQuestion(answers);
  let overview: AnswerBlockFragment | undefined;
  let currSectionBlock: SectionItem = {
    id: null,
    number: 0,
    answers: [],
  };
  const sections: SectionItem[] = [currSectionBlock];
  groupedByQuestion.forEach(({ bodyBlock }) => {
    if (!bodyBlock) {
      return;
    }
    if (bodyBlock.question?.questionType === QuestionType.Section) {
      const nextNumber = currSectionBlock.number + 1;
      currSectionBlock = {
        id: bodyBlock.id,
        number: nextNumber,
        answers: [],
      };
      sections.push(currSectionBlock);
    } else if (bodyBlock.question?.questionType === QuestionType.Overview) {
      overview = bodyBlock;
    } else {
      currSectionBlock.answers.push(bodyBlock);
    }
  });

  return { overview, sections };
}
